/**
 * TIRCircularDisplay - Large TIR Percentage Display for Dashboard (Part 2)
 *
 * Shows the Time in Range percentage prominently with a circular progress indicator
 */

import { Box, CircularProgress, Skeleton, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics'
import { useGlucoseDisplay } from '../../hooks/useGlucoseDisplay'
import { GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS } from '../../types/dashboard'
import { calculateTIRStats } from '../../utils/tirStats'

export interface TIRCircularDisplayProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  size?: number
  targetMin?: number
  targetMax?: number
  highMax?: number
}

export const TIRCircularDisplay: React.FC<TIRCircularDisplayProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    size = 200,
    targetMin,
    targetMax,
    highMax,
  }) => {
    const translate = useTranslate()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const { glucoseUnit, formatValue } = useGlucoseDisplay()
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId

    // Use the unified metrics hook that handles virtual devices
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: 10000 },
    })

    // Calculate TIR percentage
    const tirStats = useMemo(() => {
      const computedStats = calculateTIRStats(
        metricsData,
        startDate,
        endDate,
        targetMin,
        targetMax,
        highMax,
      )
      if (!computedStats) return null
      const tir = computedStats.tir

      // Determine color based on TIR value
      let color: string = TIR_ZONE_COLORS.target // Green for good TIR
      if (tir < 50) {
        color = TIR_ZONE_COLORS.veryHigh // Red for poor TIR
      } else if (tir < 70) {
        color = TIR_ZONE_COLORS.high // Orange for moderate TIR
      }

      return {
        tir,
        color,
        total: computedStats.total,
        inRange: computedStats.inRangeCount,
      }
    }, [metricsData, startDate, endDate, targetMin, targetMax, highMax])

    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: size,
          }}
        >
          <Skeleton variant="circular" width={size} height={size} />
        </Box>
      )
    }

    if (error || !tirStats) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: size,
          }}
        >
          <Typography color="text.secondary" variant="body2">
            {translate('pos.dashboard.no_data_available')}
          </Typography>
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* Background circle */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={4}
            sx={{
              color: '#e0e0e0',
              position: 'absolute',
            }}
          />
          <CircularProgress
            variant="determinate"
            value={tirStats.tir}
            size={size}
            thickness={4}
            sx={{
              color: tirStats.color,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h2"
              component="div"
              sx={{
                fontWeight: 700,
                color: tirStats.color,
                lineHeight: 1,
              }}
            >
              {tirStats.tir.toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {translate('pos.glucose.time_in_range')}
            </Typography>
          </Box>
        </Box>

        {/* Additional info */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {translate('pos.glucose.target_range')}:{' '}
            {formatValue(targetMin ?? GLUCOSE_THRESHOLDS.TARGET_MIN)} -{' '}
            {formatValue(targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX)} {glucoseUnit}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {tirStats.total} {translate('pos.glucose.readings')}
          </Typography>
        </Box>
      </Box>
    )
  },
)

export default TIRCircularDisplay
